import React, { useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { guestFilterLabel } from "../../lib/dashboardFormat";
import {
  buildCompactAnalyticsLine,
  buildDanceFormStats,
  countBeginnerLevelDancers,
  countMatchingGuests,
  filterGuestsBySearch,
  hasAnalytics,
  matchesGuestFilter,
  sortGuests,
} from "../../lib/dashboardAnalytics";
import { isLikelyComing, isTentativeOrDeclined } from "../../lib/rsvp";
import { promoteEventCoAdmin } from "../../lib/schools";
import {
  buildEventCoAdminWhatsAppMessage,
  buildRemoveRsvpWhatsAppMessage,
} from "../../lib/schoolFeeMessages";
import { ReadableNavButton } from "../Sticker";
import DangerConfirmModal from "../DangerConfirmModal";
import WhatsAppFollowUpPrompt from "../WhatsAppFollowUpPrompt";
import DashboardStatusLine from "./DashboardStatusLine";
import DashboardShareActions from "./DashboardShareActions";
import GuestAnalyticsPanel from "./GuestAnalyticsPanel";
import GuestListControls from "./GuestListControls";
import DashboardGuestRow from "./DashboardGuestRow";
import DashboardTentativeGuestList from "./DashboardTentativeGuestList";
import GuestListExportModal from "./GuestListExportModal";

export default function OverviewTab({
  data, counts, L, F, lpct, cap, copyLink, shareEvent, copyHypeBlurb,
  eventId, tokenParam, occurrenceDate, eventName, account, organizer, onRsvpRemoved,
  listRefreshNotice = "", favouriteInsights = null,
  demoMode = false, onDemoRemoveRsvp,
  showShareActions = true,
  guestFilterMenuOpen,
  onGuestFilterMenuOpenChange,
}) {
  organizer = account ?? organizer;
  const [removingId, setRemovingId] = useState(null);
  const [guestToRemove, setGuestToRemove] = useState(null);
  const [guestFilter, setGuestFilter] = useState(null);
  const [guestSearch, setGuestSearch] = useState("");
  const [guestSort, setGuestSort] = useState("name");
  const [exportOpen, setExportOpen] = useState(false);
  const [promotingId, setPromotingId] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [internalFilterMenuOpen, setInternalFilterMenuOpen] = useState(false);
  const filterMenuOpen = guestFilterMenuOpen ?? internalFilterMenuOpen;
  const setFilterMenuOpen = onGuestFilterMenuOpenChange ?? setInternalFilterMenuOpen;

  const canManage = Boolean(data?.is_hosting);
  const isEventHost = Boolean(organizer?.id && data?.organizer_id === organizer.id);
  const hostName = data?.organizer_name || organizer?.name || "";
  const schoolName = data?.host_school?.name || data?.school_external_name || "";

  async function handleMakeCoAdmin(r) {
    setPromotingId(r.dancer_id);
    try {
      await promoteEventCoAdmin(eventId, { dancerId: r.dancer_id });
      toast.success(`${r.name} is now an event co-admin.`);
      setFollowUp({
        title: "Co-admin added",
        description: `Let ${r.name || "them"} know they can help host.`,
        phone: r.phone,
        message: buildEventCoAdminWhatsAppMessage({
          guestName: r.name,
          eventName,
          hostName,
          schoolName,
        }),
        testId: "event-coadmin-whatsapp",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPromotingId(null);
    }
  }

  async function removeRsvp(r) {
    if (!r) return;
    setRemovingId(r.dancer_id);
    try {
      if (demoMode) {
        onDemoRemoveRsvp?.(r);
        toast.success(`Removed ${r.name} from the guest list`);
        setGuestToRemove(null);
      } else {
        await api.delete(`/events/${eventId}/rsvp/${r.dancer_id}`, {
          params: {
            token: tokenParam || undefined,
            occurrence_date: occurrenceDate || undefined,
          },
        });
        toast.success(`Removed ${r.name} from the guest list`);
        setGuestToRemove(null);
        onRsvpRemoved();
      }
      setFollowUp({
        title: "Guest removed",
        description: `Optionally tell ${r.name || "them"} they were removed.`,
        phone: r.phone,
        message: buildRemoveRsvpWhatsAppMessage({
          guestName: r.name,
          eventName,
          hostName,
          schoolName,
        }),
        testId: "remove-rsvp-whatsapp",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemovingId(null);
    }
  }

  const comingRsvps = (data.rsvps || []).filter(isLikelyComing);
  const tentativeRsvps = (data.rsvps || []).filter(isTentativeOrDeclined);
  const firstTimerCount = comingRsvps.filter((r) => r.first_timer).length;
  const hiddenNameCount = comingRsvps.filter((r) => r.hide_name).length;
  const beginnerLevelCount = countBeginnerLevelDancers(comingRsvps);
  const danceFormStats = buildDanceFormStats(comingRsvps);
  const compactAnalyticsSegments = buildCompactAnalyticsLine({
    firstTimerCount,
    beginnerLevelCount,
    danceFormStats,
    hiddenNameCount,
  });
  const matchingGuestCount = countMatchingGuests(comingRsvps, guestFilter);
  const cohortFilteredRsvps = guestFilter
    ? comingRsvps.filter((r) => matchesGuestFilter(r, guestFilter))
    : comingRsvps;
  const hiddenByFilterCount = guestFilter ? comingRsvps.length - cohortFilteredRsvps.length : 0;
  const displayedRsvps = useMemo(() => {
    const searched = filterGuestsBySearch(cohortFilteredRsvps, guestSearch);
    return sortGuests(searched, guestSort);
  }, [cohortFilteredRsvps, guestSearch, guestSort]);
  const filterAnnouncement = useMemo(() => {
    const parts = [];
    if (guestFilter) {
      parts.push(`Showing ${matchingGuestCount} guests filtered by ${guestFilterLabel(guestFilter)}.`);
    }
    const query = guestSearch.trim();
    if (query) {
      parts.push(`${displayedRsvps.length} shown for name search "${query}".`);
    }
    return parts.join(" ");
  }, [guestFilter, matchingGuestCount, guestSearch, displayedRsvps.length]);

  function selectGuestFilter(next) {
    setGuestFilter(next);
  }

  function clearGuestFilter() {
    setGuestFilter(null);
  }

  const listHeading = guestFilter
    ? `${matchingGuestCount} matching${hiddenByFilterCount > 0 ? ` · ${hiddenByFilterCount} hidden` : ""}`
    : `${comingRsvps.length} on the list`;

  const showGuestFilter = hasAnalytics({
    firstTimerCount,
    beginnerLevelCount,
    danceFormStats,
    hiddenNameCount,
  });

  function renderListHeaderActions(filterTrigger = null) {
    return (
      <div className="flex items-center gap-2 shrink-0">
        {filterTrigger}
        {comingRsvps.length > 0 && (
          <ReadableNavButton
            type="button"
            onClick={() => setExportOpen(true)}
            className="inline-flex items-center gap-1.5 text-[13px] px-3 py-2 min-h-[44px]"
            data-testid="dash-export-guest-list-btn"
            aria-label="Export guest list as CSV"
          >
            <Download className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </ReadableNavButton>
        )}
        {guestFilter && (
          <button
            type="button"
            onClick={clearGuestFilter}
            className="font-ui font-semibold text-[13px] text-plum underline underline-offset-2 min-h-[44px] px-2"
            data-testid="clear-guest-filter"
          >
            Show everyone
          </button>
        )}
      </div>
    );
  }

  const listHeadingBlock = (
    <h2
      id="dash-guest-list-heading"
      className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight tabular-nums text-ink min-w-0"
    >
      {listHeading}
    </h2>
  );

  return (
    <div
      className={`max-w-page mx-auto px-4 mt-6 flex flex-col gap-4 sm:pb-2 ${
        showShareActions
          ? "pb-[max(4.5rem,calc(2.75rem+env(safe-area-inset-bottom)))]"
          : "pb-2"
      }`}
    >
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="dash-filter-live-region">
        {listRefreshNotice ? `${listRefreshNotice} ` : ""}
        {filterAnnouncement}
      </div>

      <DashboardStatusLine
        counts={counts}
        favouriteCount={favouriteInsights?.summary?.favourite_count}
        showFavourites={Boolean(data.recurring && data.recurring !== "none")}
        L={L}
        F={F}
        lpct={lpct}
        cap={cap}
        waitlisted={data.waitlisted}
        balanceMessage={data.balance_message}
      />

      {showShareActions && (
        <DashboardShareActions
          shareEvent={shareEvent}
          copyLink={copyLink}
          copyHypeBlurb={copyHypeBlurb}
        />
      )}

      <section
        className="flex flex-col gap-3"
        aria-labelledby="dash-guest-list-heading"
        data-testid="dash-guest-list-panel"
      >
        <div className="pb-2 border-b border-black/[0.08]">
          {showGuestFilter ? (
            <GuestAnalyticsPanel
              segments={compactAnalyticsSegments}
              danceFormStats={danceFormStats}
              guestFilter={guestFilter}
              onSelectFilter={selectGuestFilter}
              onClearFilter={clearGuestFilter}
              menuOpen={filterMenuOpen}
              onMenuOpenChange={setFilterMenuOpen}
            >
              {({ trigger, panel }) => (
                <>
                  <div className="flex items-center justify-between gap-3">
                    {listHeadingBlock}
                    {renderListHeaderActions(trigger)}
                  </div>
                  {panel}
                </>
              )}
            </GuestAnalyticsPanel>
          ) : (
            <div className="flex items-center justify-between gap-3">
              {listHeadingBlock}
              {renderListHeaderActions()}
            </div>
          )}
          {(guestFilter || guestSearch.trim()) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              {guestFilter && (
                <span
                  className="inline-flex items-center gap-0.5 rounded-btn pl-2 pr-1 py-0.5 font-semibold text-[11px] sm:text-[12px] bg-plum text-white"
                  data-testid="active-guest-filter"
                >
                  {guestFilterLabel(guestFilter)}
                  <button
                    type="button"
                    onClick={clearGuestFilter}
                    className="inline-flex items-center justify-center min-w-[28px] min-h-[28px] rounded-sm hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    aria-label="Clear filter"
                    data-testid="clear-guest-filter-chip"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
                  </button>
                </span>
              )}
              {guestSearch.trim() && (
                <span className="font-ui font-medium text-[13px] text-ink/70 tabular-nums">
                  {displayedRsvps.length} shown for &ldquo;{guestSearch.trim()}&rdquo;
                </span>
              )}
            </div>
          )}
        </div>

        {comingRsvps.length > 0 && (
          <GuestListControls
            search={guestSearch}
            onSearchChange={setGuestSearch}
            sort={guestSort}
            onSortChange={setGuestSort}
          />
        )}

        <div className="space-y-3" data-testid="dash-guest-list">
          {comingRsvps.length === 0 ? (
            <p className="font-ui font-medium text-[15px] text-ink/70">
              No one on the list yet. Share the event to collect RSVPs.
            </p>
          ) : cohortFilteredRsvps.length === 0 ? (
            <p className="font-ui font-medium text-[15px] text-ink/70">
              No guests match this filter.{" "}
              <button
                type="button"
                onClick={clearGuestFilter}
                className="font-semibold text-plum underline underline-offset-2 min-h-[44px]"
                data-testid="clear-guest-filter-empty"
              >
                Show everyone
              </button>
            </p>
          ) : displayedRsvps.length === 0 ? (
            <p className="font-ui font-medium text-[15px] text-ink/70">
              No guests match this search.{" "}
              <button
                type="button"
                onClick={() => setGuestSearch("")}
                className="font-semibold text-plum underline underline-offset-2 min-h-[44px]"
                data-testid="clear-guest-search-empty"
              >
                Clear search
              </button>
            </p>
          ) : (
            <div className="space-y-2">
              {displayedRsvps.map((r) => (
                <DashboardGuestRow
                  key={r.id}
                  r={r}
                  removing={removingId === r.dancer_id}
                  onRequestRemove={() => setGuestToRemove(r)}
                  isHost={canManage}
                  onMakeCoAdmin={isEventHost ? handleMakeCoAdmin : undefined}
                  promoting={promotingId === r.dancer_id}
                  hostName={hostName}
                  schoolName={schoolName}
                />
              ))}
            </div>
          )}

          {tentativeRsvps.length > 0 && (
            <DashboardTentativeGuestList
              tentativeRsvps={tentativeRsvps}
              removingId={removingId}
              onRequestRemove={setGuestToRemove}
              hostName={hostName}
              schoolName={schoolName}
            />
          )}
        </div>
      </section>

      <DangerConfirmModal
        open={!!guestToRemove}
        title="Remove from guest list?"
        message={
          guestToRemove
            ? `Remove ${guestToRemove.name} from this event? They'll lose their spot and may need to RSVP again.`
            : ""
        }
        confirmLabel="Remove from list"
        onClose={() => !removingId && setGuestToRemove(null)}
        onConfirm={() => removeRsvp(guestToRemove)}
        busy={!!removingId}
        testId="remove-guest-modal"
      />

      {exportOpen && (
        <GuestListExportModal
          onClose={() => setExportOpen(false)}
          eventId={eventId}
          eventName={eventName}
          tokenParam={tokenParam}
          canExportAllEvents={Boolean(organizer)}
          demoMode={demoMode}
          demoRsvps={data.rsvps}
        />
      )}

      {followUp ? (
        <WhatsAppFollowUpPrompt
          title={followUp.title}
          description={followUp.description}
          phone={followUp.phone}
          message={followUp.message}
          onClose={() => setFollowUp(null)}
          testId={followUp.testId}
        />
      ) : null}
    </div>
  );
}
